import { DOCUMENT } from '@angular/common';
import { Directive, ElementRef, inject, Input, NgZone, OnDestroy } from '@angular/core';
import { animationFrameScheduler, auditTime, filter, fromEvent, Subscription } from 'rxjs';

import { runAfterFramePaint } from '@blaze-shared/shared-util';

@Directive({
  selector: '[blazeScrollSync]',
  standalone: false,
})
export class ScrollSyncDirective implements OnDestroy {
  private document = inject<Document>(DOCUMENT);
  private ngZone = inject(NgZone);
  private elementRef = inject(ElementRef);

  @Input() enabled = true;
  @Input() initializeOnce = true;

  private scrollFilter: Record<number, boolean> = {};
  private subscription = new Subscription();
  private isInitialized = false;

  @Input('blazeScrollSync') set scrollContainer(
    value: HTMLElement | string | HTMLElement[] | string[],
  ) {
    if (this.initializeOnce && this.isInitialized) {
      return;
    }
    const elements: HTMLElement[] = (Array.isArray(value) ? value : [value])
      .map((el) => {
        if (typeof el === 'string') {
          return this.document.querySelector(el);
        }
        return el;
      })
      .filter((element): element is HTMLElement => Boolean(element));

    if (elements.length > 0) {
      this.attachListeners([...elements, this.elementRef.nativeElement]);
      const inputLength = Array.isArray(value) ? value.length : 1;
      if (elements.length === inputLength) {
        this.isInitialized = true;
      }
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private attachListeners(elements: HTMLElement[]): void {
    this.subscription.unsubscribe();
    this.subscription = new Subscription();

    this.ngZone.runOutsideAngular(() => {
      elements.forEach((scrolledElement, index) => {
        this.subscription.add(
          fromEvent(scrolledElement, 'scroll')
            .pipe(
              filter(() => this.enabled && !this.scrollFilter[index]),
              auditTime(0, animationFrameScheduler),
            )
            .subscribe(() => {
              elements
                .filter((element) => element !== scrolledElement)
                .forEach((element) => {
                  const currentElementIndex = elements.indexOf(element);
                  this.scrollFilter[currentElementIndex] = true;
                  element.classList.add('blaze-scrolling');
                  this.updatePosition(scrolledElement, element);
                  this.ngZone.runOutsideAngular(() => {
                    runAfterFramePaint(() => {
                      this.scrollFilter[currentElementIndex] = false;
                      element.classList.remove('blaze-scrolling');
                    });
                  });
                });
            }),
        );
      });
    });
  }

  private updatePosition(scrollable: HTMLElement, target: HTMLElement): void {
    const scrolledWidthPercent =
      scrollable.scrollLeft / (scrollable.scrollWidth - scrollable.clientWidth);
    const left = scrolledWidthPercent * (target.scrollWidth - target.clientWidth);

    target.scrollTo({
      behavior: 'instant' as any,
      left,
    });
  }
}
